import { GoogleMap, Marker, InfoWindow, Polyline, useLoadScript } from '@react-google-maps/api';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './App.css';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

const PUTANJA_OPCIJE = {
  strokeColor: 'red',
  strokeOpacity: 1,
  strokeWeight: 3.0,
};

const SARAJEVO = { lat: 43.8563, lng: 18.4131 };

const jeBroj = (v) => v !== null && v !== '' && !Number.isNaN(parseFloat(v));

// Ikonica markera na mapi: /assets/icons/navbar/icon_{tip}_foreground.png
const getIconUrl = (tip) => {
  const normalized = (tip || '').toString().trim().toLowerCase().replace(/\s+/g, '_');

  const iconMap = {
    ker_posavski_gonic: 'icon_ker_posavski_gonic_foreground.png',
    ker_srpski_trobojni_gonic: 'icon_ker_srpski_trobojni_gonic_foreground.png',
    ker_istarski_kratkodlaki_gonic: 'icon_ker_istarski_kratkodlaki_gonic_foreground.png',
    ker_istarski_ostrodlaki_gonic: 'icon_ker_istarski_ostrodlaki_gonic_foreground.png',
    ker_balkanski_gonic: 'icon_ker_balkanski_gonic_foreground.png',
    ker_bosanski_ostrodlaki_gonic: 'icon_ker_bosanski_ostrodlaki_gonic_foreground.png',
    ker_crnogorski_planinski_gonic: 'icon_ker_crnogorski_planinski_gonic_foreground.png',
    ker_brak_jazavcar: 'icon_ker_brak_jazavcar_foreground.png',
    ker_slovacki_kopov: 'icon_ker_slovacki_kopov_foreground.png',
    ker_plot: 'icon_ker_plot_foreground.png',
    ker_bigl: 'icon_ker_bigl_foreground.png',
    ker_nlt: 'icon_ker_nlt_foreground.png',
    krava: 'icon_krava_foreground.png',
    bik: 'icon_bik_foreground.png',
    ovca: 'icon_ovca_foreground.png',
    koza: 'icon_koza_foreground.png',
    konj: 'icon_konj_foreground.png',
    auto: 'icon_auto_foreground.png',
    motocikl: 'icon_motocikl_foreground.png',
    bicikl: 'icon_bicikl_foreground.png',
    smartphone: 'icon_smartphone_foreground.png',
  };

  const filename = iconMap[normalized] || 'icon_ker_balkanski_gonic_foreground.png';
  return `/assets/icons/navbar/${filename}`;
};

// Putanje po IMEI-ju, čuvane u Map objektu
const usePaths = (initialValue) => {
  const [devicepaths, setDevicePaths] = React.useState(new Map(initialValue));

  const actions = React.useMemo(
    () => ({
      setPath: (key, value) =>
        setDevicePaths((prevMap) => {
          const nextMap = new Map(prevMap);
          nextMap.set(key, value);
          return nextMap;
        }),
      clearPath: () => setDevicePaths(new Map()),
    }),
    [setDevicePaths]
  );

  return [devicepaths, actions];
};

// ----------------------------------------------------------------------
// Mapa svih uređaja koji su danas bili online.
// Putanje se učitavaju automatski po otvaranju stranice, po nekoliko uređaja istovremeno,
// da se server ne optereti (umjesto ranijih nasumičnih kašnjenja do 3 minute).

const ISTOVREMENO = 5; // koliko uređaja se učitava paralelno

// ----------------------------------------------------------------------
// Gradijent putanje: prva boja = najstarija tačka, druga boja = najnovija tačka.
// Nazivi su isti kao vrijednosti kolone bojaputanje u bazi.

const BOJE = {
  plava: '#1E88E5',
  crvena: '#E53935',
  zelena: '#43A047',
  zuta: '#FDD835',
  cian: '#00BCD4',
  narandzasta: '#FB8C00',
  ljubicasta: '#8E24AA',
  roza: '#EC407A',
  tirkizna: '#1DE9B6',
};

const GRADIJENTI = {
  'Plavo-Crvena': [BOJE.plava, BOJE.crvena],
  'Zeleno-Zuta': [BOJE.zelena, BOJE.zuta],
  'Zuto-Crvena': [BOJE.zuta, BOJE.crvena],
  'Cian-Zelena': [BOJE.cian, BOJE.zelena],
  'Plavo-Cian': [BOJE.plava, BOJE.cian],
  'Narandzasto-Plava': [BOJE.narandzasta, BOJE.plava],
  'Zuto-Ljubicasta': [BOJE.zuta, BOJE.ljubicasta],
  'Ljubicasto-Zuta': [BOJE.ljubicasta, BOJE.zuta],
  'Roza-Zelena': [BOJE.roza, BOJE.zelena],
  'Tirkizno-Crvena': [BOJE.tirkizna, BOJE.crvena],
};

const DEFAULT_GRADIJENT = GRADIJENTI['Plavo-Crvena'];

// Najviše ovoliko obojenih dijelova po putanji (više = glađi prelaz, sporija mapa)
const BROJ_DIJELOVA = 20; // manje nego na Mapa.js jer je ovdje mnogo uređaja

// Interpolacija između dvije hex boje, t od 0 do 1
function interpoliraj(od, doBoje, t) {
  const a = parseInt(od.slice(1), 16);
  const b = parseInt(doBoje.slice(1), 16);
  const kanal = (shift) => {
    const x = (a >> shift) & 255;
    const y = (b >> shift) & 255;
    return Math.round(x + (y - x) * t);
  };
  const boja = (kanal(16) << 16) | (kanal(8) << 8) | kanal(0);
  return `#${boja.toString(16).padStart(6, '0')}`;
}

// Putanja podijeljena na dijelove, svaki sa svojom bojom.
// Susjedni dijelovi dijele krajnju tačku, pa linija nema prekida.
function GradijentPutanja({ path, bojaputanje }) {
  const dijelovi = useMemo(() => {
    if (!path || path.length < 2) return [];
    const [pocetna, krajnja] = GRADIJENTI[String(bojaputanje || '').trim()] || DEFAULT_GRADIJENT;

    const brojDijelova = Math.min(BROJ_DIJELOVA, path.length - 1);
    const korak = (path.length - 1) / brojDijelova;

    const rezultat = [];
    for (let i = 0; i < brojDijelova; i += 1) {
      const od = Math.round(i * korak);
      const doIndeksa = Math.round((i + 1) * korak);
      rezultat.push({
        tacke: path.slice(od, doIndeksa + 1),
        boja: interpoliraj(pocetna, krajnja, brojDijelova === 1 ? 1 : i / (brojDijelova - 1)),
      });
    }
    return rezultat;
  }, [path, bojaputanje]);

  return dijelovi.map((d, i) => (
    <Polyline
      key={i}
      path={d.tacke}
      options={{ ...PUTANJA_OPCIJE, strokeColor: d.boja }}
    />
  ));
}


// Mali krug za svaku zabilježenu tačku
const tackaIkona = (boja) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: 4,
  fillColor: boja,
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 1,
});

function MapaLive() {
  const [activeMarker, setActiveMarker] = useState(null);
  const [onlinedevices, setOnlineDevices] = useState([]);
  const [markeri, setMarkeri] = useState([]);

  const [devicepaths, { setPath }] = usePaths([]);
  const [devicetacke, { setPath: setTacke }] = usePaths([]); // tačke po IMEI-ju, s vremenom i bojom

  // --- Kontrola statusa i tracking-a ---
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [trackingStatus, setTrackingStatus] = useState('idle'); // idle | running | done
  const [processedCount, setProcessedCount] = useState(0);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  // Učitavanje online uređaja SAMO jednom, pri otvaranju stranice
  useEffect(() => {
    const getOnlineDevices = async () => {
      setLoadingDevices(true);
      try {
        const response = await axios.get(`${SERVER_URL}/devices?onlinetoday=1`);
        setOnlineDevices(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.log('Greška pri učitavanju uređaja', err);
      } finally {
        setLoadingDevices(false);
      }
    };

    getOnlineDevices();
  }, []);

  const getPathforDevice = async (device) => {
    try {
      const response = await axios.get(`${SERVER_URL}/gpsrecords?IMEI=${device.IMEI}`);
      const zapisi = (Array.isArray(response.data) ? response.data : [])
        .filter((z) => jeBroj(z.lat) && jeBroj(z.lng))
        .map((z) => ({ ...z, lat: parseFloat(z.lat), lng: parseFloat(z.lng) }))
        .sort((a, b) => String(a.datumvrijemelokacije).localeCompare(String(b.datumvrijemelokacije)))
        // ista koordinata ponovo poslana = stara lokacija bez fixa; zadrži samo prvu (kao Android)
        .filter(function bezPonavljanja(z) {
          const k = `${z.lat},${z.lng}`;
          if (this.has(k)) return false;
          this.add(k);
          return true;
        }, new Set());

      if (!zapisi.length) return;

      setPath(
        device.IMEI,
        zapisi.map(({ lat, lng }) => ({ lat, lng }))
      );

      // Svaka tačka dobija boju gradijenta prema svom mjestu u putanji
      const [bojaOd, bojaDo] = GRADIJENTI[String(device.bojaputanje || '').trim()] || DEFAULT_GRADIJENT;
      setTacke(
        device.IMEI,
        zapisi.map((z, i) => ({
          kljuc: `t-${device.IMEI}-${z.id ?? i}`,
          redniBroj: i + 1,
          ukupno: zapisi.length,
          position: { lat: z.lat, lng: z.lng },
          vrijeme: z.datumvrijemelokacije || z.vrijemelokacije,
          boja: interpoliraj(bojaOd, bojaDo, zapisi.length > 1 ? i / (zapisi.length - 1) : 1),
        }))
      );

      const zadnji = zapisi[zapisi.length - 1];

      setMarkeri((prev) => [
        ...prev.filter((m) => m.device.IMEI !== device.IMEI), // izbjegni duplikat za isti uređaj
        {
          id: zadnji.id,
          device,
          vlasnik: device.subscriber,
          position: { lat: zadnji.lat, lng: zadnji.lng },
          vrijeme: zadnji.datumvrijemelokacije || zadnji.vrijemelokacije,
        },
      ]);
    } catch (err) {
      console.log('Greška pri učitavanju putanje za', device.IMEI, err);
    }
  };

  // Automatsko učitavanje putanja čim stignu uređaji: po ISTOVREMENO uređaja u talasu
  useEffect(() => {
    if (!onlinedevices.length) return undefined;

    let prekinuto = false;

    const ucitajSve = async () => {
      setTrackingStatus('running');
      setProcessedCount(0);

      for (let i = 0; i < onlinedevices.length; i += ISTOVREMENO) {
        if (prekinuto) return;
        const talas = onlinedevices.slice(i, i + ISTOVREMENO);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(
          talas.map(async (device) => {
            await getPathforDevice(device);
            if (!prekinuto) setProcessedCount((prev) => prev + 1);
          })
        );
      }

      if (!prekinuto) setTrackingStatus('done');
    };

    ucitajSve();

    // Ako korisnik napusti stranicu, prekini daljnje učitavanje
    return () => {
      prekinuto = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlinedevices]);

  const handleOnLoad = (map) => {
    map.setMapTypeId('satellite');
  };

  const handleActiveMarker = (marker) => {
    if (marker === activeMarker) return;
    setActiveMarker(marker);
  };

  return (
    <>
      <div style={{ padding: '10px', fontFamily: 'sans-serif' }}>
        {loadingDevices && <p>Učitavanje uređaja...</p>}

        {!loadingDevices && (
          <p>
            Uređaja online danas: {onlinedevices.length}. Prikazano na mapi: {markeri.length}.
            {trackingStatus === 'running' && ` Učitavam putanje... (${processedCount}/${onlinedevices.length})`}
          </p>
        )}
      </div>

      {isLoaded && (
        <GoogleMap
          mapContainerClassName="map-container"
          center={SARAJEVO}
          zoom={7}
          onLoad={handleOnLoad}
          onClick={() => setActiveMarker(null)}
        >
          {onlinedevices.map((device) =>
            devicepaths.get(device.IMEI) ? (
              <GradijentPutanja
                key={device.IMEI}
                path={devicepaths.get(device.IMEI)}
                bojaputanje={device.bojaputanje}
              />
            ) : null
          )}

          {/* Svaka zabilježena tačka: mali krug u boji gradijenta, klik pokazuje vrijeme */}
          {onlinedevices.map((device) =>
            (devicetacke.get(device.IMEI) || []).map((t) => (
              <Marker
                key={t.kljuc}
                position={t.position}
                onClick={() => handleActiveMarker(t.kljuc)}
                icon={tackaIkona(t.boja)}
              >
                {activeMarker === t.kljuc ? (
                  <InfoWindow position={t.position} onCloseClick={() => setActiveMarker(null)}>
                    <div id="iw-container">
                      <div className="iw-title">{device.ime}</div>
                      <div className="iw-content">
                        <p>
                          Tačka {t.redniBroj} / {t.ukupno}
                        </p>
                        <p>Vrijeme: {t.vrijeme}</p>
                      </div>
                    </div>
                  </InfoWindow>
                ) : null}
              </Marker>
            ))
          )}

          {markeri.map(({ id, device, vlasnik, position, vrijeme }) => (
            <Marker
              key={device.IMEI}
              position={position}
              onClick={() => handleActiveMarker(id)}
              icon={{
                url: getIconUrl(device.tip),
                scaledSize: new window.google.maps.Size(50, 50),
              }}
            >
              {activeMarker === id ? (
                <InfoWindow position={position} onCloseClick={() => setActiveMarker(null)}>
                  <div id="iw-container">
                    <div className="iw-title">{device.ime}</div>
                    <div className="iw-content">
                      <p>Model: {device.tiptrackera}</p>
                      <p>IMEI: {device.IMEI}</p>
                      <p>
                        Vlasnik: {vlasnik?.ime} {vlasnik?.prezime}
                      </p>
                      <p>Vrijeme: {vrijeme}</p>
                    </div>
                  </div>
                </InfoWindow>
              ) : null}
            </Marker>
          ))}
        </GoogleMap>
      )}
    </>
  );
}

export default MapaLive;